import { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import {
  getAdmissionStatus, cancelAdmission, acceptOffer, type AdmissionStatus,
} from '../../lib/admissions';
import { ApiError } from '../../lib/apiClient';
import { formatDateShort } from '../../lib/format';
import { useCopy } from '../../copy/CopyProvider';
import './AdmissionStatus.css';

/**
 * One page for every admission status.
 *
 * ── ONE ROUTE, NOT ONE PER WORKFLOW ──────────────────────────────────────────────────
 *
 * §54. A request and a waitlist entry are different records with different lifecycles, but
 * the question somebody arrives with is the same one — "where do I stand?" — and the answer
 * has the same shape. Two routes would mean two pages to keep in step, and a person who
 * cannot remember which kind they submitted would have to guess which URL to open.
 *
 * ── THE ID IS NOT THE CREDENTIAL ─────────────────────────────────────────────────────
 *
 * §53/§86. The token comes from the URL (the emailed link) or from this browser's own
 * memory of submitting it. Without one the server answers 404 — not 403, because saying
 * "that exists but is not yours" would confirm the record exists to somebody walking ids.
 */
export function AdmissionStatusPage() {
  const { admissionId } = useParams<{ admissionId: string }>();
  const [params] = useSearchParams();
  const t = useCopy();

  // The emailed link first, then what this browser remembers. The email is the durable
  // copy; localStorage is a convenience for the device that submitted it.
  const urlToken = params.get('token');

  const [status, setStatus] = useState<AdmissionStatus | null>(null);
  const [token, setToken] = useState<string | null>(urlToken);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState<{ orderId: string; accessToken: string | null } | null>(null);

  const load = useCallback(async () => {
    if (!admissionId) return;
    setLoading(true);
    // The emailed link first; failing that, what this browser stored when it submitted.
    // Recall is keyed by event and this page only knows the admission id, so the stored
    // entries are scanned for a matching one.
    const attempt = urlToken ?? findRememberedToken(admissionId);
    setToken(attempt);
    try {
      const result = await getAdmissionStatus(admissionId, attempt);
      setStatus(result);
      setError(null);
    } catch (err) {
      setStatus(null);
      setError(err instanceof ApiError && err.status === 404
        ? 'We could not find that admission. The link may have expired, or it may belong to '
          + 'a different browser. Check the email we sent you.'
        : 'That status could not be loaded just now.');
    } finally {
      setLoading(false);
    }
  }, [admissionId, urlToken]);

  useEffect(() => { void load(); }, [load]);

  async function cancel() {
    if (!admissionId) return;
    if (!window.confirm('Withdraw this request? You can submit a new one afterwards.')) return;
    setBusy(true);
    try {
      await cancelAdmission(admissionId, token);
      setFlash('Your request has been withdrawn.');
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? describe(err) : 'That could not be withdrawn.');
    } finally { setBusy(false); }
  }

  async function accept() {
    if (!admissionId) return;
    setBusy(true);
    try {
      const result = await acceptOffer(admissionId, token);
      setConfirmed({ orderId: result.orderId, accessToken: result.accessToken });
      setFlash('Your place is confirmed. Your tickets are on their way by email.');
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? describe(err) : 'That place could not be confirmed.');
    } finally { setBusy(false); }
  }

  if (loading) return <div className="adm-status"><p>Loading…</p></div>;

  if (!status) {
    return (
      <div className="adm-status">
        <h1>Admission status</h1>
        <p className="alert alert--error" role="alert">{error}</p>
        <Link className="button button--ghost" to="/events">Browse events</Link>
      </div>
    );
  }

  const state = describeState(status, t);

  return (
    <div className="adm-status">
      <p className="adm-status__eyebrow">
        {status.kind === 'request' ? 'Your request' : 'Your waitlist place'}
      </p>
      <h1>{status.event.title}</h1>
      {status.event.date && (
        <p className="adm-status__when">{formatDateShort(status.event.date)}</p>
      )}

      {flash && <p className="alert alert--success" role="status">{flash}</p>}
      {error && <p className="alert alert--error" role="alert">{error}</p>}

      <div className={`adm-status__state is-${state.tone}`}>
        <span className="adm-status__label">{state.label}</span>
        {state.body && <p>{state.body}</p>}
        {/* An operator's own message on a decline. Never an internal note. */}
        {status.message && <p className="adm-status__message">{status.message}</p>}
      </div>

      <dl className="adm-status__facts">
        <div>
          <dt>People</dt>
          <dd>{status.partySize}</dd>
        </div>
        <div>
          <dt>{status.kind === 'request' ? 'Submitted' : 'Joined'}</dt>
          <dd>{formatDateShort(status.submittedAt ?? status.joinedAt ?? '')}</dd>
        </div>
        {status.offerExpiresAt && (
          <div>
            <dt>Offer held until</dt>
            <dd>{new Date(status.offerExpiresAt).toLocaleString()}</dd>
          </div>
        )}
      </dl>

      {/* §23: no queue position anywhere on this page, for either kind. */}

      {status.status === 'offered' && (
        <button type="button" className="button button--primary button--block"
          disabled={busy} onClick={() => { void accept(); }}>
          {busy ? 'Confirming…' : 'Accept your place'}
        </button>
      )}

      {(status.orderId || confirmed) && (
        <Link
          className="button button--primary button--block"
          to={confirmed?.accessToken
            ? `/orders/${confirmed.orderId}/tickets?token=${encodeURIComponent(confirmed.accessToken)}`
            : `/orders/${status.orderId}/tickets`}
        >
          View your tickets
        </Link>
      )}

      {status.kind === 'request' && status.status === 'pending' && (
        <button type="button" className="button button--ghost button--block"
          disabled={busy} onClick={() => { void cancel(); }}>
          Withdraw this request
        </button>
      )}

      <p className="adm-status__footnote">
        <Link to={`/events/${status.eventId}`}>Back to the event</Link>
        {' · '}
        Keep the link from your email: it is how you get back to this page on another device.
      </p>
    </div>
  );
}

/**
 * This state, in words.
 *
 * ── THE SENTENCES THAT HAVE A COPY KEY COME FROM COPY ────────────────────────────────
 *
 * The wording an operator might want to change — what a declined request says, what being
 * on a waitlist means — is registered in the Copy Layer and read through it, so the page and
 * the email that says the same thing cannot drift apart. The rest are structural labels
 * describing a machine state, and §33 keeps those out of Copy.
 */
function describeState(
  status: AdmissionStatus, t: ReturnType<typeof useCopy>,
): { label: string; body: string | null; tone: 'neutral' | 'good' | 'bad' | 'wait' } {
  const key = `${status.kind}:${status.status}`;

  if (key === 'request:declined') {
    return {
      label: 'Not confirmed',
      body: t('admissions.request.declined.body', "Unfortunately we couldn't fit this request in."),
      tone: 'bad',
    };
  }
  if (key === 'waitlist:waiting') {
    return {
      label: t('admissions.waitlist.joined.heading', "You're on the waitlist"),
      // §23: no position, here or anywhere.
      body: t('admissions.waitlist.joined.body', "We'll email you if a place opens up."),
      tone: 'wait',
    };
  }

  return DESCRIPTIONS[key] ?? { label: status.status, body: null, tone: 'neutral' };
}

/**
 * Every other state, in words.
 *
 * Keyed by kind AND status because the two lifecycles genuinely differ: `declined` happens
 * to a request and never to a queue position, `offered` happens to a queue position and
 * never to a request.
 */
const DESCRIPTIONS: Record<string, {
  label: string; body: string | null; tone: 'neutral' | 'good' | 'bad' | 'wait';
}> = {
  'request:pending': {
    label: 'Awaiting review',
    body: 'We have your request. Submitting one does not guarantee admission, and we will '
      + 'email you either way.',
    tone: 'wait',
  },
  'request:approved': {
    label: 'Approved',
    body: 'Your place is confirmed and your tickets have been emailed to you.',
    tone: 'good',
  },
  'request:cancelled': {
    label: 'Withdrawn',
    body: 'You withdrew this request. You can submit a new one if the event is still open.',
    tone: 'neutral',
  },
  'request:expired': {
    label: 'Expired',
    body: 'This request was not reviewed before the event closed.',
    tone: 'neutral',
  },
  'waitlist:offered': {
    label: 'A place is available',
    body: 'Accept it below to confirm. Places are held only until the deadline shown.',
    tone: 'good',
  },
  'waitlist:accepted': {
    label: 'Confirmed',
    body: 'Your place is confirmed and your tickets have been emailed to you.',
    tone: 'good',
  },
  'waitlist:declined': { label: 'Declined', body: 'You turned this place down.', tone: 'neutral' },
  'waitlist:expired': {
    label: 'Offer expired',
    body: 'The offer was not accepted in time. Contact the organiser if you would still like '
      + 'to come.',
    tone: 'bad',
  },
  'waitlist:removed': {
    label: 'Removed',
    body: 'This place was removed by the organiser.',
    tone: 'neutral',
  },
};

function describe(err: ApiError): string {
  const payload = err.payload as { message?: string } | null;
  return payload?.message ?? 'That could not be completed.';
}

/** Look through what this browser remembers for a token matching this admission. */
function findRememberedToken(admissionId: string): string | null {
  try {
    const raw = window.localStorage.getItem('voleto.admissions');
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    for (const entry of Object.values(parsed as Record<string, { admissionId?: string; token?: string }>)) {
      if (entry?.admissionId === admissionId && typeof entry.token === 'string') return entry.token;
    }
    return null;
  } catch {
    return null;
  }
}
